import { Flex } from "@/components/atoms/flex";
import { CallInProgressActionButtons } from "@/components/molecules/interactionWorkspace";
import { useIntl } from "react-intl";
import { mergeClasses, Text } from "@fluentui/react-components";
import { useMemo, useCallback } from "react";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import Timer from "@/components/atoms/timer/Timer";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { CallType } from "@/common/models/callSession";
import { CallSessionHelper } from "@/common/helpers";
import { MonitorTypes } from "@/repository/models/monitoring";
import { useInteractionBarCommonStyle } from "@/common/style/interactionBarCommonStyle";

export const CallInteractionActions = () => {
	const intl = useIntl();
	const interactionBarCommonStyle = useInteractionBarCommonStyle();

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const getMonitoringOperation = useCallback(() => {
		switch (callSession?.monitorType) {
			case MonitorTypes.Silent:
				return intl.formatMessage({
					id: `Interaction.Operations.Listen`,
				});
			case MonitorTypes.BargeIn:
				return intl.formatMessage({
					id: `Interaction.Operations.BargeIn`,
				});
			default:
				return "";
		}
	}, [callSession]);

	const operation = useMemo<string>(() => {
		switch (callSession?.state) {
			case null:
			case AgentInteractionStatus.Active:
			case AgentInteractionStatus.WarmTransferInviteRejected:
			case AgentInteractionStatus.OutboundAcceptedByAgent:
			case AgentInteractionStatus.Paused:
				const callType =
					callSession.callType == CallType.Incoming ? "Ongoing" : "Outbound";

				return intl.formatMessage({
					id: `Interaction.Operations.${callType}Call`,
				});
			case AgentInteractionStatus.WarmTransferInviteSent:
				return callSession?.isIVRWarmTransfer
					? intl.formatMessage({ id: "Transfer.WarmTransferIVRStarted" })
					: intl.formatMessage({ id: "Transfer.Ringing3rdParty" });
			case AgentInteractionStatus.AcceptedWarmTransferInvitation:
			case AgentInteractionStatus.WarmTransferInviteAccepted:
				return intl.formatMessage({
					id: `Interaction.Operations.Transferring`,
				});
			case AgentInteractionStatus.WarmTransferConsultingCaller:
				return intl.formatMessage({
					id: `Interaction.Operations.ConsultingCaller`,
				});
			case AgentInteractionStatus.WarmTransferConsultingAgent:
				return intl.formatMessage({
					id: `Interaction.Operations.ConsultingAgent`,
				});
			case AgentInteractionStatus.InitiatedWarmTransferConferenceMode:
			case AgentInteractionStatus.PartOfWarmTransferConferenceMode:
				return intl.formatMessage({
					id: `Interaction.Operations.ConferenceModeInitiated`,
				});
			case AgentInteractionStatus.Monitoring:
				return getMonitoringOperation();
			default:
				return "";
		}
	}, [callSession]);

	const operationPartner = useMemo<string>(() => {
		switch (callSession?.state) {
			case AgentInteractionStatus.WarmTransferInviteAccepted:
			case AgentInteractionStatus.WarmTransferConsultingAgent:
				return callSession.secondaryAgentName || "";
			case AgentInteractionStatus.WarmTransferConsultingCaller:
				return callSession.caller?.name || "";
			default:
				return "";
		}
	}, [callSession?.state]);

	return (
		<div
			className={mergeClasses(
				interactionBarCommonStyle.interactionText,
				interactionBarCommonStyle.interactionTimerAndButtonsContainer
			)}
		>
			<Flex className={interactionBarCommonStyle.interactionActionContainer}>
				<Flex column className={interactionBarCommonStyle.interactionAction}>
					<Text align="end" className={interactionBarCommonStyle.actionText}>
						{`${operation} ${operationPartner}`}
					</Text>
					<Text
						align="end"
						className={interactionBarCommonStyle.actionText}
						title={CallSessionHelper.resourceName(callSession)}
					>
						{CallSessionHelper.resourceName(callSession)}
					</Text>
				</Flex>
				<Flex className={interactionBarCommonStyle.timer}>
					<Timer
						className={interactionBarCommonStyle.timer}
						startDate={currentInteraction?.startDate}
					/>
				</Flex>
			</Flex>
			<CallInProgressActionButtons />
		</div>
	);
};
